import {
  Box,
  Tooltip,
  TooltipProps,
  Typography,
  tooltipClasses,
} from '@mui/material'
import { grey } from '@mui/material/colors'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import PercentileChartElementBarDivider from './BarDivider'
import {
  PercentileChartBarBackgroundColor,
  PercentileChartCohortBackgroundColor,
  PercentileChartElementBarColors,
} from 'components/ReportElement'
import { styled } from '@mui/material/styles'
import getPercentileLabel from 'util/getPercentilLabel'

const DefaultArrowSpacing = 10
const TransitionSpeed = 1000
const ArrowSize = 6
const containerBorderSize = 1
const DefaultPadding = 8

export type PercentileBarColorOptions =
  'primary' | 'secondary' | 'static' | 'primaryTan' | 'secondaryGreen'

export const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: grey[50],
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: `1px solid ${grey[300]}`,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: grey[50],
  },
  [`& .${tooltipClasses.arrow}::before`]: {
    border: `1px solid ${grey[300]}`,
  },
}))

interface Props {
  percentage: number
  showBoldValue?: boolean
  showBoldPercentile?: boolean
  showNormalValue?: boolean
  showNormalPercentile?: boolean
  label: string
  value: number
  size: number
  color: PercentileBarColorOptions
  prefix?: string
  suffix?: string
  significantDigits?: number
  significance?: boolean
}

type HtmlTooltipProps = {
  showTooltipLabel?: boolean
  tooltipValue: 'value' | 'percentile'
} & Props

export const PercentileChartElementBarWithTooltip: React.FC<
  HtmlTooltipProps
> = (props) => {
  const percentage = Math.round(props.percentage)
  const toFixedDigits =
    props.significantDigits || props.significantDigits === 0
      ? props.significantDigits
      : 2
  const percentileLabel = getPercentileLabel(percentage)
  return (
    <HtmlTooltip
      arrow
      placement="top"
      followCursor={true}
      title={
        <React.Fragment>
          {props.showTooltipLabel && (
            <Typography align="center" fontSize="0.8rem">
              {props.label}
            </Typography>
          )}
          <Typography align="center">
            {props.tooltipValue === 'percentile'
              ? percentileLabel
              : `${props.prefix}${props.value.toFixed(toFixedDigits)}${
                  props.suffix
                }${props.significance ? '*' : ''}`}
          </Typography>
        </React.Fragment>
      }
    >
      <Box>
        <PercentileChartElementBar {...props} />
      </Box>
    </HtmlTooltip>
  )
}

const PercentileChartElementBar: React.FC<Props> = ({
  percentage,
  showBoldValue = false,
  showBoldPercentile = false,
  showNormalValue = false,
  showNormalPercentile = false,
  label,
  value,
  size,
  color,
  prefix = '',
  suffix = '',
  significantDigits,
  significance = false,
}) => {
  percentage = Math.round(percentage)
  const tooltipRef = useRef<HTMLElement>()
  const containerRef = useRef<HTMLElement>()
  const barRef = useRef<HTMLElement>()
  const labelRef = useRef<HTMLElement>()

  const [showRightArrow, setShowRightArrow] = useState(false)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showBottomArrow, setShowBottomArrow] = useState(true)

  const [containerWidth, setContainerWidth] = useState(0)
  const [barWidth, setBarWidth] = useState<number | undefined>()
  const [tooltipWidth, setTooltipWidth] = useState(0)

  const [labelWidth, setLabelWidth] = useState(0)
  const [labelHeight, setLabelHeight] = useState(0)

  useEffect(() => {
    setBarWidth((percentage / 100) * containerWidth)
  }, [containerWidth, percentage])

  useEffect(() => {
    if (barWidth != undefined && tooltipWidth > 0 && labelWidth > 0) {
      const barElement = barRef.current as HTMLElement
      const tooltipElement = tooltipRef.current as HTMLElement
      const labelElement = labelRef.current as HTMLElement

      labelElement.style.marginTop = `-${labelHeight / 2}px`
      barElement.style.width = `${percentage}%`
      tooltipElement.style.left = `${percentage}%`

      const halfTooltipWidth = tooltipWidth / 2
      if (labelWidth >= barWidth - tooltipWidth) {
        if (tooltipWidth >= barWidth) {
          labelElement.style.left = `${
            barWidth + tooltipWidth + DefaultArrowSpacing + DefaultPadding
          }px`
        } else {
          labelElement.style.left = `${
            barWidth + halfTooltipWidth + DefaultPadding
          }px`
        }
        labelElement.style.maxWidth = `${
          containerWidth - (barWidth + halfTooltipWidth + DefaultPadding)
        }px`
        labelElement.style.color = 'rgba(0, 0, 0, 0.87)'
        labelElement.style.backgroundColor = PercentileChartBarBackgroundColor
      } else {
        labelElement.style.left = `${DefaultPadding}px`
        labelElement.style.color = color === 'secondary' ? '#FFFFFF' : ''
        labelElement.style.backgroundColor =
          PercentileChartElementBarColors[color]
      }
      if (barWidth + tooltipWidth >= containerWidth) {
        tooltipElement.style.marginLeft = `-${
          tooltipWidth + DefaultArrowSpacing
        }px`
        tooltipElement.style.top = `${DefaultArrowSpacing / 2}px`
        setShowBottomArrow(false)
        setShowLeftArrow(false)
        setShowRightArrow(true)
      } else if (tooltipWidth >= barWidth) {
        tooltipElement.style.marginLeft = `${DefaultArrowSpacing}px`
        tooltipElement.style.top = `${DefaultArrowSpacing / 2}px`
        setShowBottomArrow(false)
        setShowRightArrow(false)
        setShowLeftArrow(true)
      } else {
        setShowBottomArrow(true)
        tooltipElement.style.top = '0px'
        tooltipElement.style.marginLeft = `-${halfTooltipWidth}px`
      }
    }
  }, [
    barWidth,
    tooltipWidth,
    containerWidth,
    labelWidth,
    labelHeight,
    percentage,
    color,
  ])

  useLayoutEffect(() => {
    if (
      tooltipRef.current &&
      containerRef.current &&
      barRef.current &&
      labelRef.current
    ) {
      const tooltipElement = tooltipRef.current as HTMLElement
      setTooltipWidth(tooltipElement.offsetWidth)

      const containerElement = containerRef.current as HTMLElement
      setContainerWidth(containerElement.offsetWidth)

      const labelElement = labelRef.current as HTMLElement
      setLabelWidth(labelElement.offsetWidth)
      setLabelHeight(labelElement.offsetHeight)
    }
  }, [tooltipRef, containerRef, barRef, labelRef, percentage, label, value])

  const containerSx = {
    width: '100%',
    height: `${size}px`,
    backgroundColor: PercentileChartBarBackgroundColor,
    borderRadius: '4px',
    overflow: 'hidden',
    // border: `${containerBorderSize}px solid #BDBDBD`,
    border: `${containerBorderSize}px solid #FFFFFF`,
    position: 'relative',
  }

  const containerBorderSx = {
    width: '100%',
    // height: `${size - 2}px`,
    border: `${containerBorderSize}px solid #BDBDBD`,
    borderRadius: '4px',
    backgroundColor: 'transparent',
    zIndex: '0',
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  }

  const barSx = {
    width: '0px',
    height: '100%',
    backgroundColor: PercentileChartElementBarColors[color],
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    zIndex: '0',
    transition: `width ${TransitionSpeed}ms`,
  }

  const labelSx = {
    position: 'absolute',
    left: `${DefaultPadding}px`,
    top: '50%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    fontSize: '14px',
    lineHeight: '1',
    zIndex: '2',
    borderRadius: color === 'static' ? '2px' : null,
    backgroundColor: PercentileChartElementBarColors[color],
    transition: `left ${TransitionSpeed}ms`,
    color: color === 'secondary' ? '#FFFFFF' : null,
    padding: '4px 8px',
  }

  const tooltipHeight = size - containerBorderSize * 2 - DefaultArrowSpacing
  const tooltipContainerSx = {
    transition: `left ${TransitionSpeed}ms`,
    position: 'absolute',
    left: '0',
    top: '0',
    height: `${tooltipHeight}px`,
    borderRadius: '4px',
    backgroundColor: grey[800],
    color: 'white',
    fontWeight: 'bold',
    fontSize: '0.8em',
    padding: `0px ${DefaultPadding}px`,
    zIndex: '2',
    '&:after': {
      display: showBottomArrow ? 'block' : 'none',
      position: 'absolute',
      top: `${tooltipHeight}px`,
      left: '50%',
      marginLeft: `-${ArrowSize}px`,
      content: '""',
      width: '0',
      height: '0',
      borderLeft: `${ArrowSize}px solid transparent`,
      borderRight: `${ArrowSize}px solid transparent`,
      borderTop: `${ArrowSize}px solid ${grey[800]}`,
    },
    '&:before': {
      display: !showBottomArrow ? 'block' : 'none',
      position: 'absolute',
      top: `${tooltipHeight / 2}px`,
      left: showLeftArrow ? `-${ArrowSize}px` : null,
      right: showRightArrow ? `-${ArrowSize}px` : null,
      marginTop: `-${ArrowSize}px`,
      content: '""',
      width: '0',
      height: '0',
      borderTop: `${ArrowSize}px solid transparent`,
      borderRight: showLeftArrow ? `${ArrowSize}px solid ${grey[800]}` : null,
      borderLeft: showRightArrow ? `${ArrowSize}px solid ${grey[800]}` : null,
      borderBottom: `${ArrowSize}px solid transparent`,
    },
  }

  const toFixedDigits =
    significantDigits || significantDigits === 0 ? significantDigits : 2

  const percentileLabel = getPercentileLabel(percentage)

  return (
    <Box sx={containerSx} ref={containerRef}>
      <Box sx={containerBorderSx}></Box>
      <PercentileChartElementBarDivider
        barPercentage={percentage}
        dividerPercentage={25}
        color={color}
      />
      <PercentileChartElementBarDivider
        barPercentage={percentage}
        dividerPercentage={50}
        color={color}
      />
      <PercentileChartElementBarDivider
        barPercentage={percentage}
        dividerPercentage={75}
        color={color}
      />

      <Box sx={barSx} ref={barRef}></Box>

      <Box sx={labelSx} ref={labelRef}>
        {label}
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        sx={tooltipContainerSx}
        ref={tooltipRef}
      >
        {(showBoldPercentile || showBoldValue) && (
          <Typography
            component={'strong'}
            sx={{ fontSize: '20px', fontWeight: 'bold' }}
          >
            {showBoldPercentile && percentileLabel}
            {showBoldValue &&
              `${prefix}${value.toFixed(toFixedDigits)}${suffix}${
                significance ? '*' : ''
              }`}
          </Typography>
        )}
        {(showNormalPercentile || showNormalValue) && (
          <Typography component={'span'} sx={{ fontSize: '14px' }}>
            {showNormalValue &&
              `${prefix}${value.toFixed(toFixedDigits)}${suffix}${
                significance ? '*' : ''
              }`}
            {showNormalPercentile && percentileLabel}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default PercentileChartElementBar
